import type { Signal } from "@preact/signals";
import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { IcoImage, IcoSpinner, IcoX } from "../icons";
import { ImageCropper } from "./image-cropper";

type UploadConfig = {
  /** Function to get presigned POST URL and form fields */
  getUploadUrl: () => Promise<{
    url: string;
    fields: Record<string, string>;
    key: string;
    maxSize: number;
  }>;
  /** Function to confirm upload after success */
  confirmUpload: () => Promise<{
    success: boolean;
    path: string;
    url?: string | null;
  }>;
};

type ImageUploadProps = {
  /** Signal to bind the selected file to */
  file?: Signal<File | null>;
  /** Signal to bind the preview URL to (for display) */
  previewUrl?: Signal<string | null>;
  /** Existing image URL (for editing existing images) */
  initialUrl?: string | null;
  /** Accepted file types */
  accept?: string;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Label shown in the upload zone */
  label?: string;
  /** Help text shown below the label */
  helpText?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** S3 upload configuration */
  upload?: UploadConfig;
  /** Called when upload completes successfully */
  onUploadComplete?: (path: string) => void;
  /** Enable image cropping before upload (default true when upload is provided) */
  enableCrop?: boolean;
  /** Crop output size in pixels (default 256) */
  cropSize?: number;
};

/**
 * Rectangular image upload component with drag-and-drop support.
 * Shows a dashed border drop zone, and a preview once an image is selected.
 *
 * When upload config is provided, handles the full S3 presigned POST flow:
 * 1. User selects/drops image
 * 2. Image cropper modal opens (if enableCrop is true)
 * 3. On confirm, gets presigned URL from server
 * 4. Uploads to S3 via presigned POST
 * 5. Confirms upload with server to update database
 */
export function ImageUpload({
  file,
  previewUrl: externalPreviewUrl,
  initialUrl,
  accept = "image/png,image/jpeg,image/gif,image/webp",
  maxSize = 2 * 1024 * 1024, // 2MB default
  label = "Click to upload",
  helpText = "PNG, JPG, or GIF up to 2MB",
  disabled = false,
  upload,
  onUploadComplete,
  enableCrop,
  cropSize = 256,
}: ImageUploadProps) {
  const internalPreviewUrl = useSignal<string | null>(initialUrl ?? null);
  const previewUrl = externalPreviewUrl ?? internalPreviewUrl;
  const isDragging = useSignal(false);
  const error = useSignal<string | null>(null);
  const isUploading = useSignal(false);

  // File pending crop (shown in cropper modal)
  const pendingCropFile = useSignal<File | null>(null);

  // Whether to show the cropper - default to true when upload is provided
  const shouldCrop = enableCrop ?? !!upload;

  // Clean up object URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl.value && previewUrl.value !== initialUrl) {
        URL.revokeObjectURL(previewUrl.value);
      }
    };
  }, []);

  function handleFileSelect(selectedFile: File | null) {
    error.value = null;

    if (!selectedFile) {
      // Clear the file
      if (previewUrl.value && previewUrl.value !== initialUrl) {
        URL.revokeObjectURL(previewUrl.value);
      }
      previewUrl.value = null;
      if (file) file.value = null;
      return;
    }

    // Validate file type
    const acceptedTypes = accept.split(",").map((t) => t.trim());
    const isValidType = acceptedTypes.some(
      (type) =>
        type === selectedFile.type ||
        (type.endsWith("/*") &&
          selectedFile.type.startsWith(type.replace("/*", "/"))),
    );
    if (!isValidType) {
      error.value = "Invalid file type";
      return;
    }

    // Validate file size
    if (selectedFile.size > maxSize) {
      const maxMB = Math.round(maxSize / (1024 * 1024));
      error.value = `File too large (max ${maxMB}MB)`;
      return;
    }

    // If cropping is enabled, show the cropper modal
    if (shouldCrop) {
      pendingCropFile.value = selectedFile;
      return;
    }

    // Otherwise, directly set the preview
    setPreview(selectedFile);
  }

  function setPreview(selectedFile: File | Blob) {
    // Create preview URL
    if (previewUrl.value && previewUrl.value !== initialUrl) {
      URL.revokeObjectURL(previewUrl.value);
    }
    previewUrl.value = URL.createObjectURL(selectedFile);
    if (file && selectedFile instanceof File) {
      file.value = selectedFile;
    }
  }

  async function handleUpload(blob: Blob): Promise<void> {
    if (!upload) {
      // No upload config, just set preview and close cropper
      setPreview(blob);
      pendingCropFile.value = null;
      return;
    }

    isUploading.value = true;
    error.value = null;

    try {
      // 1. Get presigned POST URL and form fields
      const { url, fields } = await upload.getUploadUrl();

      // 2. Build multipart form data with policy fields + file
      const formData = new FormData();
      // Add all policy fields first (order matters for S3)
      for (const [key, value] of Object.entries(fields)) {
        formData.append(key, value);
      }
      // File must be last
      formData.append("file", blob);

      // 3. Upload to S3 via POST
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        console.error("S3 upload failed:", response.status, text);
        throw new Error("Upload failed");
      }

      // 4. Confirm upload with server
      const result = await upload.confirmUpload();

      // 5. Update preview and notify
      setPreview(blob);
      // Use URL if available (full CDN URL), otherwise fall back to path
      onUploadComplete?.(result.url ?? result.path);

      // 6. Close the cropper on success
      pendingCropFile.value = null;
    } finally {
      isUploading.value = false;
    }
  }

  function handleCropConfirm(blob: Blob): Promise<void> {
    // Return the promise so the cropper can await it and show upload state
    return handleUpload(blob);
  }

  function handleCropCancel() {
    pendingCropFile.value = null;
  }

  function handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    handleFileSelect(input.files?.[0] ?? null);
    // Reset input so the same file can be selected again
    input.value = "";
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDragging.value = false;
    if (disabled) return;
    handleFileSelect(e.dataTransfer?.files?.[0] ?? null);
  }

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    if (!disabled) isDragging.value = true;
  }

  function handleDragLeave() {
    isDragging.value = false;
  }

  function handleRemove(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(null);
  }

  const isDisabled = disabled || isUploading.value;

  return (
    <>
      {/* Image Cropper Modal */}
      {pendingCropFile.value && (
        <ImageCropper
          source={pendingCropFile.value}
          outputSize={cropSize}
          onCrop={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}

      {previewUrl.value ? (
        <div class="relative inline-block">
          <img
            src={previewUrl.value}
            alt="Upload preview"
            class="h-20 w-auto rounded-xl object-cover border border-border"
          />
          {isUploading.value && (
            <div class="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
              <IcoSpinner class="size-6 text-white" />
            </div>
          )}
          {!isDisabled && (
            <button
              type="button"
              onClick={handleRemove}
              class="absolute -top-2 -right-2 p-1 rounded-full bg-surface border border-border shadow-sm hover:bg-surface-hover transition-colors"
              aria-label="Remove image"
            >
              <IcoX class="size-3.5 text-text-muted" />
            </button>
          )}
        </div>
      ) : (
        <div class="flex flex-col gap-1">
          <label
            class={`
              flex flex-col items-center gap-3 rounded-xl border-2 border-dashed p-8 transition-colors
              ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-text-subtle hover:bg-surface-hover"}
              ${isDragging.value ? "border-blue-500 bg-blue-500/5" : "border-border"}
              ${error.value ? "border-red-500" : ""}
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {isUploading.value ? (
              <IcoSpinner class="size-10 text-text-subtle" />
            ) : (
              <IcoImage class="size-10 text-text-subtle" />
            )}
            <div class="text-center">
              <span class="font-medium text-text-secondary">
                {isUploading.value ? "Uploading..." : label}
              </span>
              {!isUploading.value && (
                <p class="text-sm text-text-muted mt-1">{helpText}</p>
              )}
            </div>
            <input
              type="file"
              accept={accept}
              class="sr-only"
              onChange={handleInputChange}
              disabled={isDisabled}
              aria-label="Upload image"
            />
          </label>
          {error.value && <p class="text-sm text-red-400">{error.value}</p>}
        </div>
      )}
    </>
  );
}
